import { Card } from '@optools/components/ui/card';
import { Input } from '@optools/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@optools/components/ui/tabs';
import { escapeRegExp } from 'es-toolkit';
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface MappingViewProps<T> {
  activeTab?: string | null;
  filtersTitle: string;
  items: T[];
  onSearch: (term: string) => void;
  panelsTitle: string;
  renderFilter: (item: T) => ReactNode;
  renderPanel: (item: T) => ReactNode;
}

export function MappingView<T extends { name: string }>({
  activeTab,
  filtersTitle,
  items,
  onSearch,
  panelsTitle,
  renderFilter,
  renderPanel,
}: MappingViewProps<T>) {
  const [currentPanel, setCurrentPanel] = useState(items[0]?.name);
  const [currentPanelHeight, setCurrentPanelHeight] = useState<number>(0);

  const handleChangeTab = (panelName: string) => {
    setCurrentPanel(panelName);
  };

  useEffect(() => {
    setCurrentPanel((currentTab) => activeTab ?? currentTab);
  }, [activeTab]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (containerRef.current) {
      setCurrentPanelHeight(containerRef.current.clientHeight - 10);

      const handleResize = () => {
        if (containerRef.current) {
          setCurrentPanelHeight(containerRef.current.clientHeight - 10);
        }
      };

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  return (
    <Tabs
      ref={containerRef}
      className="h-full w-full flex-row rounded-md"
      orientation="vertical"
      value={currentPanel}
      onValueChange={handleChangeTab}>
      <Card className="h-full w-full overflow-hidden">
        <div className="grid h-full grid-cols-[180px_1fr]">
          <div>
            <nav className="flex flex-col" style={{ height: currentPanelHeight }}>
              <h2 className="p-6 text-xl font-bold">{filtersTitle}</h2>

              <div className="w-full overflow-y-auto pb-1">
                <TabsList
                  aria-label={`${filtersTitle}-label`}
                  aria-orientation="vertical"
                  className="flex h-full w-full flex-col justify-start overflow-y-scroll rounded-none bg-transparent p-0 pl-3 group-data-horizontal/tabs:h-auto">
                  {items.map((item) => {
                    return (
                      <TabsTrigger
                        key={item.name}
                        className="group min-h-10 w-full justify-between rounded-l-full rounded-r-none border-r-0 py-1 text-xs font-bold break-all whitespace-normal transition-colors data-[state=active]:bg-muted!"
                        value={item.name}>
                        {renderFilter(item)}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>
            </nav>
          </div>

          <div className="bg-muted">
            <div className="flex flex-col" style={{ height: currentPanelHeight }}>
              <div className="flex flex-wrap items-center justify-between gap-2 p-4">
                <h2 className="text-xl font-bold">{panelsTitle}</h2>
                <Input
                  className="max-w-80"
                  placeholder="Search"
                  onChange={(e) => onSearch(escapeRegExp(e.target.value))}
                />
              </div>
              {items.map((item) => (
                <TabsContent
                  key={item.name}
                  className="with-scrollbar overflow-y-auto px-2 pb-4"
                  value={item.name}>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-2 px-2">
                    {renderPanel(item)}
                  </div>
                </TabsContent>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </Tabs>
  );
}
